import Vue from 'vue';
import {
  reactive,
  ref,
  computed,
  watch,
  inject,
} from '@vue/composition-api';
import {
  useState,
  useActions,
} from 'vuex-composition-helpers/dist';
import {
  omit,
  cloneDeep,
  kebabCase,
} from 'lodash';
import { validate } from 'vee-validate';

import useProcessing from '@/composables/useProcessing';

import useSkeleton from '@/composables/useSkeleton';

// Vehicle related
import * as carPayloadTemplates from '@/payloadTemplates/car';
import * as vanPayloadTemplates from '@/payloadTemplates/van';
import * as bikePayloadTemplates from '@/payloadTemplates/bike';
import * as motorhomePayloadTemplates from '@/payloadTemplates/motorhome';

import {
  deepRemoveKeys,
  mapCustomFieldsToDefaults,
} from '@/helpers/common';
import {
  handleValidationError,
} from '@/helpers/bugsnag';

export default ({
  payload,
  structure,
}, { root }) => {
  const config = inject('config');
  const productName = inject('productName');
  const apiProductName = inject('apiProductName');
  const formMetadata = inject('formMetadata', null);

  const isInitialLoaderHidden = ['van'].includes(productName);

  // constants
  const elementIdToScrollTo = '#app';

  // composables
  const {
    isProcessing,
    toggleProcessing,
  } = useProcessing();
  const {
    showSkeleton,
    hideSkeleton,
  } = useSkeleton();

  const {
    toast,
    confirmModal,
  } = useState([
    'toast',
    'confirmModal',
  ]);

  const {
    showToast,
    hideToast,
    showConfirmModal,
    hideConfirmModal,
  } = useActions([
    'showToast',
    'hideToast',
    'showConfirmModal',
    'hideConfirmModal',
  ]);

  // reactive
  const form = reactive(cloneDeep(payload));

  // refs
  const isValidated = ref(false);
  const step = ref(null);
  const formActions = ref(null);
  const rules = ref(null);
  const stepData = ref(null);
  const version = ref(null);
  const isLoading = ref(false);
  const quoteLimitExceeded = ref(false);
  const currentStep = ref(1);
  const isReturningUser = ref(false);
  const isRenewalUser = ref(false);
  const isEditProcess = ref(false);
  const isContinueTriggered = ref(false);
  const formValidationObserver = ref({});
  const additionalDriverStateValidity = ref([]);

  // car related
  const currentDriver = ref(0);
  const isNewDriver = ref(false);
  const driversBackup = ref(null);

  // COMPUTED START HERE
  const prevalidateModule = computed(() => import('@/prevalidation/' + productName + '/index'));
  const stepComponent = computed(() => `step${currentStep.value}`);
  const isLastStep = computed(() => currentStep.value === structure.length);
  const recaptcha = computed(() => {
    return formActions.value.recaptcha;
  });
  const showActions = computed(() => {
    switch (productName) {
      case 'car':
      case 'van':
      case 'bike':
      case 'motorhome':
        return currentStep.value > 1 ? true : !!stepData.value?.vehicleConfirm;
      default:
        return true;
    }
  });

  const listSteps = computed(() => structure.map((step, index) => ({
    id: step.stepId,
    label: step.stepLabel,
    active: (index + 1) === currentStep.value,
  })));
  const activeStep = computed(() => listSteps.value.find(({ active }) => active) || null);

  // car related
  const componentKey = computed(() => {
    return currentStep.value === 2 ? `${stepComponent.value}-${currentDriver.value}` : `${stepComponent.value}`; // TODO maybe add project value check for car/van
  });
  const isAdditionalDriver = computed(() => (currentStep.value === 2) && (currentDriver.value > 0));

  const activeStepTitle = computed(() => {
    if (isAdditionalDriver.value) {
      // car, van, bike
      return productName === 'bike' ? 'Additional rider' : 'Additional driver';
    }

    return activeStep.value ? activeStep.value.label : '';
  });

  const formConfigData = computed(() => {
    const flags = formValidationObserver.value.flags;

    return structure.map((step, index) => {
      const isActive = activeStep.value && (activeStep.value.id === step.stepId) && !isAdditionalDriver.value;

      return {
        ...step,
        isActive,
        isClickable: isStepAvailable(step, structure[index - 1]),
        isFailed: isActive && flags?.validated && flags?.failed,
      };
    });
  });

  const fixStickyContainer = () => {
    const event = document.createEvent('Event');
    event.initEvent('resize', false, true);
    window.dispatchEvent(event);
  };

  const onValid = async () => {
    await Vue.nextTick();
    fixStickyContainer();
  };

  const setStep = index => {
    let desiredStepIndex;

    if (typeof +index === 'number' && index > 0 && index <= structure.length) {
      desiredStepIndex = +index;
    }

    const stepsNotDone = structure
      .map(({ stepId }, i) => ({
        stepId,
        index: i + 1,
      }))
      .filter(({ stepId }) => {
        if (!form[stepId]) return true;
        if (Array.isArray(form[stepId])) return !form[stepId][0]._saved;
        return !form[stepId]._saved;
      });

    if (stepsNotDone.length > 0) {
      // not all steps were saved yet
      currentStep.value = desiredStepIndex <= stepsNotDone[0].index ? desiredStepIndex : stepsNotDone[0].index;
    } else {
      // all steps have been already saved
      currentStep.value = index <= structure.length ? desiredStepIndex : structure.length;
    }
  };

  const isStepAvailable = (step, prevStep) => {
    if (!activeStep.value) return false;
    if (step.stepId === activeStep.value.id) return false;
    const stepPayload = Array.isArray(form[step.stepId]) ? form[step.stepId][0] : form[step.stepId];
    if (stepPayload._saved) return true;
    if (prevStep) {
      const prevStepPayload = Array.isArray(form[prevStep.stepId]) ? form[prevStep.stepId][0] : form[prevStep.stepId];
      if (prevStepPayload._saved) return true;
    }
    return false;
  };

  const onBeforeDriverAdd = (formObserver) => {
    saveStep();
    formObserver.reset();
    isNewDriver.value = true;
    addDriver();
  };

  const onGoToStep = async ({ index, formObserver }) => {
    const { changed } = formObserver;
    const additionalDriver = activeStep.value.id === 'drivers' && currentDriver.value > 0 && isNewDriver.value;
    isContinueTriggered.value = false;

    if (changed === false) {
      if (additionalDriver) {
        form.drivers.pop();
      }
      currentStep.value = index;
      currentDriver.value = 0;
      await Vue.nextTick();
      isValidated.value = false;
      root.$scrollTo(elementIdToScrollTo, 200);
    } else {
      showConfirmModal({
        text: 'You have unsaved changes on the current step. Continue?',
        yesCallback: async () => {
          if (additionalDriver) {
            form.drivers.pop();
            currentDriver.value = 0;
          }
          currentStep.value = index;
          isValidated.value = false;
          root.$scrollTo(elementIdToScrollTo, 200);
        },
      });
    }
  };

  const onGoToGroup = ({ group, step, formObserver }) => {
    const firstAvailableSection = group.sections.find(({ sectionId }) => document.getElementById(sectionId));
    if (group.isValid || !group.isDirty) {
      root.$scrollTo(`#${firstAvailableSection.sectionId}`, 300);
    } else {
      const invalidSections = group.sections.filter(section => {
        return section.fields
          .map(field => `${step.stepId}_${kebabCase(field)}`)
          .some(field => {
            if (!formObserver.fields[field]) return false;
            return formObserver.fields[field].invalid;
          });
      });
      const firstAvailableInvalidSection = invalidSections.find(({ sectionId }) => document.getElementById(sectionId));
      root.$scrollTo(`#${firstAvailableInvalidSection ? firstAvailableInvalidSection.sectionId : firstAvailableSection.sectionId}`, 300);
    }
  };

  /**
   * Adds _saved flag to stepData
   * @param {Object} stepData - current step data
   * @returns {Object} - stepData with _saved:true key
   */
  const getQuoteData = stepData => {
    const data = {
      _saved: true,
    };
    return Object.assign(stepData, data);
  };

  const handleApiErrors = (error) => {
    if (error?.response?.status === 422) {
      handleValidationError(error);
      return;
    }

    if (error?.response?.status === 409) {
      showToast({
        type: 'error',
        text: 'Unexpected error',
      });
    }

    // Only notify Bugsnag if it is enabled and on production environment
    if (root.$Bugsnag && process.env.NODE_ENV === 'production') {
      root.$Bugsnag.notify(error);
    } else {
      console.error(error.toString());
    }
  };

  // car related
  const prepareDriverPayload = async payload => {
    let isValid = true;
    if (!payload.licenceNumber) {
      isValid = false;
    } else {
      const { valid } = await validate(payload.licenceNumber, 'licenceNumber');
      isValid = valid;
    }
    if (!isValid) {
      return {
        ...payload,
        licenceNumber: null,
        licenceNumberYesNo: false,
      };
    }
    return payload;
  };

  const redefinedProcessJourneyPayload = (quoteData) => {
    const payload = cloneDeep(quoteData);
    const instanceRef = Array.isArray(payload) ? payload[0] : payload;
    const fieldsWithId = [
      'claims',
      'convictions',
      'specificItemsDetails',
      'bicyclesOrLaptopsDetails',
    ];

    Object.keys(instanceRef).forEach(key => {
      if (fieldsWithId.includes(key) && Array.isArray(instanceRef[key]) && instanceRef[key].length) {
        instanceRef[key] = instanceRef[key].map(item => omit(item, 'id'));
      }

      if (key === 'telephone') {
        instanceRef.telephone = instanceRef.telephone.replace('+44', '0');
      }
    });

    return payload;
  };

  const saveStep = () => {
    form[activeStep.value.id] = {
      ...cloneDeep(stepData.value),
      _saved: true,
    };
  };

  const processJourney = async (step, quoteData) => {
    let data = null;

    try {
      data = await root.$api.processJourney({
        productName: apiProductName,
        productPartnerId: config.partner,
        step: (() => {
          switch (productName) {
            case 'car':
            case 'van':
            case 'bike':
            case 'motorhome':
              if (step !== 'drivers') return step;
              return `driver${currentDriver.value === 0 ? '' : `-additional-${currentDriver.value}`}`;
            default:
              return step;
          }
        })(),
        quoteData: redefinedProcessJourneyPayload(quoteData),
      });
    } catch (error) {
      if (error?.response?.status === 422) {
        await setValidationErrors(step, error?.response?.data?.errors);

        root.$scrollTo('#form-errors', 300);
      }

      handleApiErrors(error);
    }
    return data;
  };

  const setValidationErrors = async (step, errors = {}) => {
    const result = Object.fromEntries(
      // convert to array then map and fromEntries back to object
      Object.entries(errors).map(([key, value]) => {
        const observerFieldName = `${step}_${kebabCase(key)}`;

        return [observerFieldName, value];
      }),
    );

    if (`${step}_claims` in result) {
      formValidationObserver.value.setErrors({ [`${step}_had-any-accidents`]: errors[`${step}_claims`] });
    }

    if (`${step}_convictions` in result) {
      formValidationObserver.value.setErrors({ [`${step}_had-any-convictions`]: errors[`${step}_convictions`] });
    }

    formValidationObserver.value.setErrors(result);

    return result;
  };

  const validateForm = async (formObserver) => {
    const isValid = await formObserver.validate();

    isValidated.value = true;

    // if form is not valid, scroll to the form errors section
    if (!isValid) {
      await Vue.nextTick();
      root.$scrollTo('#form-errors', 300);
      showToast({
        type: 'warning',
        text: 'Please fix all errors to continue',
      });
    }

    return isValid;
  };

  const nextStep = async (formObserver, afterSessionSaveStepHook = () => {}) => {
    toggleProcessing(true);
    const isValid = await validateForm(formObserver);

    if (productName === 'car' && activeStep.value.id === 'drivers' && currentDriver.value > 0) {
      additionalDriverStateValidity.value[currentDriver.value - 1] = { isValid };
    }
    // if form is not valid, scroll to the form errors section
    if (!isValid) {
      toggleProcessing(false);
      return;
    }

    // if multiform is in edit process set isContinueTriggered flag and watch this value inside MuliformContoller
    // if all forms inside are valid system set all updated data and allow user go further
    if (isEditProcess.value) {
      isContinueTriggered.value = true;
      await nextStep(formObserver);
      return;
    }

    let doContinue = true;

    let payload;

    // adjust current step data for journey endpoint
    switch (productName) {
      case 'car':
      case 'van':
      case 'bike':
      case 'motorhome':
        payload = activeStep.value.id === 'drivers'
          ? getQuoteData(await prepareDriverPayload(stepData.value))
          : getQuoteData(stepData.value);
        break;

      case 'home':
        payload = activeStep.value.id === 'insured'
          ? stepData.value
          : getQuoteData(stepData.value);

        break;
      case 'bicycle':
        payload = activeStep.value.id === 'bicycles'
          ? stepData.value.map(data => ({ ...data, _saved: true }))
          : getQuoteData(stepData.value);

        break;
      case 'breakdown':
        payload = activeStep.value.id === 'yourDetails'
          ? stepData.value
          : getQuoteData(stepData.value);
        break;
      default:
        payload = getQuoteData(stepData.value);
        break;
    }

    // Save step to the session
    const processJourneyResult = await processJourney(activeStep.value.id, payload);

    if (!processJourneyResult || processJourneyResult.status !== 'success') {
      doContinue = false;
    }

    // if step could not be saved - do nothing, forcing user to click nextStep again
    // try catch and error notification is located in processJourney method
    if (!doContinue) {
      toggleProcessing(false);
      return;
    }

    afterSessionSaveStepHook(stepData.value);

    // update form data with a saved step data
    // for drivers need to track a saved driver index to update it
    switch (productName) {
      case 'car':
      case 'van':
      case 'bike':
      case 'motorhome':
        if (activeStep.value.id === 'drivers') {
          form[activeStep.value.id][currentDriver.value] = {
            ...cloneDeep(stepData.value),
            _saved: true,
          };
        } else {
          saveStep();
        }
        break;
      case 'bicycle':
        if (activeStep.value.id === 'bicycles') {
          form[activeStep.value.id] = stepData.value.map(data => ({ ...data, _saved: true }));
        } else {
          saveStep();
        }
        break;
      default:
        saveStep();
        break;
    }

    // load journey data
    // note: does nothing for now, but in future process journey will be validated and may be processed somehow on the server, so we should load journey in such cases
    /* const journey = await loadJourney();
    if (journey.status === 'success') {
      const { data } = journey;
      if (data) {
        journey.value = cloneDeep(data);
      }
    } */

    toggleProcessing(false);

    // go forward if not the last step
    if (!isLastStep.value) {
      // if the current step was an additional driver we reset About You to a main proposer
      if (currentStep.value === 2 && currentDriver.value !== 0) {
        currentDriver.value = 0;
      }

      currentStep.value += 1;

      await Vue.nextTick();
      isValidated.value = false;
      root.$scrollTo(elementIdToScrollTo, 300);
    } else {
      toggleProcessing(true);

      const SID = await submit();

      if (SID) {
        await root.$router.push({
          name: `${productName}-insurance-results`,
          params: { sid: SID },
        }).catch(() => {});
      }

      toggleProcessing(false);
    }
  };

  const prevStep = async (observer) => {
    if (currentStep.value === 1) return;
    isContinueTriggered.value = false;

    const {
      changed,
    } = observer;

    if (changed === false) {
      currentStep.value -= 1;
      currentDriver.value = 0;
      await Vue.nextTick();
      isValidated.value = false;
      root.$scrollTo(elementIdToScrollTo, 200);
    } else {
      showConfirmModal({
        text: 'You have unsaved changes on the current step. Continue?',
        yesButton: 'Yes',
        noButton: 'No',
        yesCallback: async () => {
          currentStep.value -= 1;
          isValidated.value = false;
          root.$scrollTo(elementIdToScrollTo, 200);
        },
      });
    }
  };

  // for returning users: on additional driver step go back to previous driver
  const returningUserPrevStep = async (observer) => {
    const { changed } = observer;
    if (changed === false) {
      currentDriver.value -= 1;
      await Vue.nextTick();
      root.$scrollTo(elementIdToScrollTo, 200);
    } else {
      showConfirmModal({
        text: 'You have unsaved changes on the current step. Continue?',
        yesButton: 'Yes',
        noButton: 'No',
        yesCallback: async () => {
          currentDriver.value -= 1;
          root.$scrollTo(elementIdToScrollTo, 200);
        },
      });
    }
  };

  const loadJourney = async () => {
    let journey = null;
    try {
      journey = await root.$api.showJourney({
        productName: apiProductName,
        productPartnerId: config.partner,
      });
    } catch (error) {
      handleApiErrors(error);
    }
    return journey;
  };

  const submit = async () => {
    let SID = false;
    try {
      const { data } = await loadJourney();
      deepRemoveKeys(data, null, key => key.indexOf('_') === 0);

      if (step.value?.hasAdditionalDrivers === false) {
        data.drivers = [data.drivers[0]];
      }

      const response = await root.$api.getQuote(
        apiProductName,
        data,
        config.partner,
      );
      const { status } = response;

      if (status === 'success') {
        const { data } = response;
        SID = data.SID;
      }
    } catch (error) {
      handleApiErrors(error);
      if (error?.response?.data?.errors) {
        console.log(JSON.stringify(error.response.data.errors, null, 2));
        if (error.response.data.errors === 'Too many requests') {
          if (config.partnerQuoteLimitAction === 'redirect') {
            // or change window location
            window.location = config.partnerQuoteLimitData;
            return;
          }
          // show quote limit dialog and prevent further progress + reset captcha
          quoteLimitExceeded.value = true;
          recaptcha.value && recaptcha.value.reset();
          return;
        }
        alert(JSON.stringify(error.response.data.errors, null, 2));
      }
    }
    return SID;
  };

  // recaptcha
  const validateBeforeRecaptcha = async (formObserver) => {
    toggleProcessing(true);

    const isValid = await validateForm(formObserver);

    if (!isValid) {
      toggleProcessing(false);
      return;
    }

    // if form valid exec recaptcha
    recaptcha.value.execute();
    toggleProcessing(false);
  };

  // DRIVER
  const cancelEditingDriver = async () => {
    currentStep.value = 3;
    currentDriver.value = 0;

    if (isNewDriver.value) {
      const drivers = form.drivers.slice();
      drivers.pop();
      form.drivers = drivers;
    }

    await Vue.nextTick();
    root.$scrollTo(elementIdToScrollTo, 300);
  };
  const addDriver = async () => {
    switch (productName) {
      case 'car':
        form.drivers.push(cloneDeep(carPayloadTemplates.DRIVER_ADDITIONAL));
        break;
      case 'van':
        form.drivers.push(cloneDeep(vanPayloadTemplates.DRIVER_ADDITIONAL));
        break;
      case 'bike':
        form.drivers.push(cloneDeep(bikePayloadTemplates.DRIVER_ADDITIONAL));
        break;
      case 'motorhome':
        form.drivers.push(mapCustomFieldsToDefaults(formMetadata.steps[1].questions));
        break;
    }
    currentStep.value = 2;
    currentDriver.value = form.drivers.length - 1;
    await Vue.nextTick();
    isValidated.value = false;
    root.$scrollTo(elementIdToScrollTo, 300);
  };
  const changeDriver = async (index) => {
    currentStep.value = 2;
    currentDriver.value = index;
    await Vue.nextTick();
    root.$scrollTo(elementIdToScrollTo, 300);
  };
  const deleteDriver = async (index) => {
    showConfirmModal({
      yesButton: 'Ok',
      noButton: 'Cancel',
      yesCallback: async () => {
        toggleProcessing(true);
        try {
          await root.$api.processJourney({
            productName: apiProductName,
            productPartnerId: config.partner,
            step: `driver-additional-${index + 1}`,
            quoteData: 'remove',
          });

          if (step.value.stepData.mainUserOfVehicle === index + 2) {
            step.value.stepData.mainUserOfVehicle = 1;
          } else if (step.value.stepData.mainUserOfVehicle > index + 2) {
            step.value.stepData.mainUserOfVehicle -= 1;
          }

          Vue.delete(form.drivers, index + 1);
        } catch (error) {
          handleApiErrors(error);
        }
        toggleProcessing(false);
      },
    });
  };

  const init = async () => {
    if (isInitialLoaderHidden) {
      showSkeleton();
    }

    isLoading.value = true;

    const journey = await loadJourney();
    if (journey?.status === 'success') {
      const { data: dataRaw } = journey;
      if (dataRaw) {
        // set prevalidation state for additional driver on car product
        if (productName === 'car' && dataRaw.drivers?.length > 1) {
          additionalDriverStateValidity.value = dataRaw.drivers.reduce((acc, driver, index) => {
            if (!index) return acc;
            acc.push({
              isValid: !('errors' in driver),
            });
            return acc;
          }, []);
        }

        // fix data for prevalidate
        if (productName === 'car' || productName === 'van' || productName === 'bike') {
          if (dataRaw.vehicle && dataRaw.vehicle.vehicleModifications && Array.isArray(dataRaw.vehicle.vehicleModifications) && dataRaw.vehicle.vehicleModifications.length === 0) {
            dataRaw.vehicle.vehicleModifications = {};
          }
        }

        let data;

        if (
          Object.prototype.hasOwnProperty.call(await prevalidateModule.value, 'prevalidate')
        ) {
          data = await (await prevalidateModule.value).prevalidate(dataRaw, config);
        } else {
          data = dataRaw;
        }

        isReturningUser.value = data?.returningUser;
        isRenewalUser.value = data?.renewalUser;
        version.value = data.productVersion;

        structure.forEach(({ stepId }) => {
          // skip if form structure doesn't support this form data key
          if (!data[stepId]) return;

          // process items of steps with array type
          if (Array.isArray(data[stepId])) {
            form[stepId] = [];
            data[stepId].forEach((stepItem, index) => {
              switch (productName) {
                case 'car':
                  form[stepId].push(
                    Object.assign({}, cloneDeep(index === 0 ? carPayloadTemplates.DRIVER : carPayloadTemplates.DRIVER_ADDITIONAL), stepItem),
                  );
                  break;
                case 'van':
                  form[stepId].push(
                    Object.assign({}, cloneDeep(index === 0 ? vanPayloadTemplates.DRIVER : vanPayloadTemplates.DRIVER_ADDITIONAL), stepItem),
                  );
                  break;
                case 'bike':
                  form[stepId].push(
                    Object.assign({}, cloneDeep(index === 0 ? bikePayloadTemplates.DRIVER : bikePayloadTemplates.DRIVER_ADDITIONAL), stepItem),
                  );
                  break;
                case 'motorhome':
                  form[stepId].push(
                    Object.assign({}, cloneDeep(index === 0 ? motorhomePayloadTemplates.DRIVER : motorhomePayloadTemplates.DRIVER_ADDITIONAL), stepItem),
                  );
                  break;
                default:
                  // todo
                  form[stepId].push(Object.assign({}, cloneDeep(stepItem)));
                  break;
              }
            });
          } else {
            Object.keys(data[stepId]).forEach(key => {
              form[stepId][key] = data[stepId][key];
            });
          }
        });

        setStep(isReturningUser.value ? 1 : root.$route.query.step);

        watch(currentStep, async val => {
          if (root.$route.query.step && +root.$route.query.step === val) return;
          await root.$router.replace({
            query: {
              ...(root.$route.query || {}),
              step: val,
            },
          }, () => {});
        }, { immediate: true });
      }
    }

    // Needed for development purposes - increases phpsessid lifetime
    if (process.env.NODE_ENV === 'development') {
      setInterval(loadJourney, 30 * 60 * 1000);
    }

    isLoading.value = false;
    hideSkeleton();
  };

  const patchObserver = formObserver => {
    return {
      ...formObserver,
      validated: isValidated.value,
    };
  };

  return {
    // main data
    config,
    form,
    stepData,
    [`${productName}Structure`]: structure,

    // states
    currentStep,
    currentDriver,
    isLoading,
    isNewDriver,
    isAdditionalDriver,
    isReturningUser,
    isInitialLoaderHidden,
    isRenewalUser,
    quoteLimitExceeded,
    additionalDriverStateValidity,

    // computed
    stepComponent,
    isLastStep,
    listSteps,
    activeStepTitle,
    formConfigData,
    componentKey,
    showActions,
    activeStep,

    // methods
    onValid,
    nextStep,
    prevStep,
    returningUserPrevStep,
    init,
    validateBeforeRecaptcha,
    saveStep,
    patchObserver,

    // driver
    cancelEditingDriver,
    changeDriver,
    deleteDriver,
    onBeforeDriverAdd,

    // sidebar
    onGoToStep,
    onGoToGroup,

    // other
    isProcessing,
    toast,
    showToast,
    hideToast,
    confirmModal,
    hideConfirmModal,
    showConfirmModal,
    isEditProcess,
    isContinueTriggered,

    // refs
    isValidated,
    step,
    formActions,
    rules,
    formValidationObserver,

    driversBackup,
  };
};
